(function() {
  'use strict';

  var SWIPE_THRESHOLD = 40;
  var NATIVE_SCROLL_END_MS = 160;
  var AUTOPLAY_RESUME_MS = 450;

  function CustomTrackCarousel(root) {
    this.root = root;
    this.viewport = root.querySelector('.custom-track-carousel__viewport');
    this.track = root.querySelector('.custom-track-carousel__track');
    this.dots = Array.from(root.querySelectorAll('.custom-track-carousel__dot'));
    this.prevBtn = root.querySelector('.custom-track-carousel__arrow--prev');
    this.nextBtn = root.querySelector('.custom-track-carousel__arrow--next');
    this.currentIndex = 0;
    this.autoplayTimer = null;
    this.isPaused = false;
    this.isDragging = false;
    this.isJumping = false;
    this.isUserScrolling = false;
    this.userInteracting = false;
    this.pointerId = null;
    this.startX = 0;
    this.startScrollLeft = 0;
    this.slides = [];
    this.realSlideCount = 0;

    this.autoplayEnabled = root.dataset.autoplay === 'true';
    this.autoplaySpeed = (parseInt(root.dataset.autoplaySpeed, 10) || 5) * 1000;
    this.pauseOnHover = root.dataset.pauseOnHover === 'true';
    this.loopEnabled = root.dataset.loop !== 'false';
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.onDotClick = this.onDotClick.bind(this);
    this.onPrevClick = this.onPrevClick.bind(this);
    this.onNextClick = this.onNextClick.bind(this);
    this.onScroll = this.onScroll.bind(this);
    this.onPointerDown = this.onPointerDown.bind(this);
    this.onPointerMove = this.onPointerMove.bind(this);
    this.onPointerUp = this.onPointerUp.bind(this);
    this.onPointerCancel = this.onPointerCancel.bind(this);
    this.onTouchStart = this.onTouchStart.bind(this);
    this.onTouchEnd = this.onTouchEnd.bind(this);
    this.onMouseEnter = this.onMouseEnter.bind(this);
    this.onMouseLeave = this.onMouseLeave.bind(this);
    this.onFocusIn = this.onFocusIn.bind(this);
    this.onFocusOut = this.onFocusOut.bind(this);
    this.onVisibilityChange = this.onVisibilityChange.bind(this);
    this.onResize = this.onResize.bind(this);
    this.onBreakpointChange = this.onBreakpointChange.bind(this);

    if (!this.viewport || !this.track) return;

    this.init();
  }

  CustomTrackCarousel.prototype.init = function() {
    this.setupSlides();
    this.updateDimensions();
    this.adjustCardContentSpacing();
    this.currentIndex = 0;
    this.syncUi();
    this.bindEvents();
    this.scrollToLogicalIndex(0, true);

    if (this.autoplayEnabled && this.realSlideCount > 1 && !this.reducedMotion) {
      this.startAutoplay();
    }
  };

  CustomTrackCarousel.prototype.setupSlides = function() {
    this.removeClones();

    this.realSlides = Array.from(
      this.track.querySelectorAll('.custom-track-carousel__slide:not(.is-clone)')
    );
    this.realSlideCount = this.realSlides.length;

    if (this.loopEnabled && this.realSlideCount > 1) {
      var firstClone = this.realSlides[0].cloneNode(true);
      var lastClone = this.realSlides[this.realSlideCount - 1].cloneNode(true);

      firstClone.classList.add('is-clone');
      lastClone.classList.add('is-clone');
      firstClone.removeAttribute('id');
      lastClone.removeAttribute('id');
      firstClone.querySelectorAll('[id]').forEach(function(el) {
        el.removeAttribute('id');
      });
      lastClone.querySelectorAll('[id]').forEach(function(el) {
        el.removeAttribute('id');
      });

      this.track.insertBefore(lastClone, this.realSlides[0]);
      this.track.appendChild(firstClone);
    }

    this.slides = Array.from(this.track.querySelectorAll('.custom-track-carousel__slide'));
    this.loopOffset = this.loopEnabled && this.realSlideCount > 1 ? 1 : 0;
  };

  CustomTrackCarousel.prototype.removeClones = function() {
    if (!this.track) return;
    this.track.querySelectorAll('.custom-track-carousel__slide.is-clone').forEach(function(clone) {
      clone.remove();
    });
  };

  CustomTrackCarousel.prototype.getCols = function() {
    var mobile = window.matchMedia('(max-width: 749px)').matches;
    var style = getComputedStyle(this.root);
    var cols = parseFloat(style.getPropertyValue(mobile ? '--ctc-cols-mobile' : '--ctc-cols'));
    if (isNaN(cols) || cols < 1) cols = mobile ? 1 : 3;
    return cols;
  };

  CustomTrackCarousel.prototype.getGap = function() {
    var style = getComputedStyle(this.root);
    var gap = parseFloat(style.getPropertyValue('--ctc-gap'));
    return isNaN(gap) ? 16 : gap;
  };

  CustomTrackCarousel.prototype.getPeekFactor = function() {
    var peek = this.root.dataset.peek === 'true';
    if (!peek) return 1;
    var style = getComputedStyle(this.root);
    var factor = parseFloat(style.getPropertyValue('--ctc-peek-factor'));
    return isNaN(factor) || factor <= 0 ? 0.88 : factor;
  };

  CustomTrackCarousel.prototype.updateDimensions = function() {
    var vpWidth = this.viewport.clientWidth;
    if (vpWidth <= 0) return;

    var mobile = window.matchMedia('(max-width: 749px)').matches;
    var style = getComputedStyle(this.root);
    var width;

    var pxWidth = style.getPropertyValue(mobile ? '--ctc-card-width-px-mobile' : '--ctc-card-width-px').trim();
    if (!pxWidth || pxWidth === '0px') {
      pxWidth = style.getPropertyValue('--ctc-card-width-px').trim();
    }

    if (pxWidth && pxWidth !== '0px') {
      width = parseFloat(pxWidth);
    } else {
      var customWidth = style.getPropertyValue(mobile ? '--ctc-card-width-custom-mobile' : '--ctc-card-width-custom').trim();
      if (!customWidth || customWidth === '0px' || customWidth === '0%') {
        customWidth = style.getPropertyValue('--ctc-card-width-custom').trim();
      }

      if (customWidth && customWidth !== '0px' && customWidth !== '0%') {
        if (customWidth.indexOf('%') !== -1) {
          width = (vpWidth * parseFloat(customWidth)) / 100;
        } else {
          width = parseFloat(customWidth);
        }
      } else {
        var cols = this.getCols();
        var gap = this.getGap();
        var peek = this.getPeekFactor();
        width = ((vpWidth - gap * (cols - 1)) / cols) * peek;
      }
    }

    width = Math.max(width, 120);
    var edgePad = Math.max(0, (vpWidth - width) / 2);

    for (var i = 0; i < this.slides.length; i++) {
      this.slides[i].style.flexBasis = width + 'px';
      this.slides[i].style.width = width + 'px';
    }

    this.track.style.paddingLeft = edgePad + 'px';
    this.track.style.paddingRight = edgePad + 'px';

    this.slideWidth = width;
    this.edgePad = edgePad;
  };

  CustomTrackCarousel.prototype.isMobile = function() {
    return window.matchMedia('(max-width: 749px)').matches;
  };

  CustomTrackCarousel.prototype.useNativeTouchScroll = function() {
    return this.isMobile();
  };

  CustomTrackCarousel.prototype.scheduleAutoplayResume = function() {
    var self = this;
    clearTimeout(this.autoplayResumeTimer);
    this.autoplayResumeTimer = setTimeout(function() {
      self.userInteracting = false;
      self.restartAutoplay();
    }, AUTOPLAY_RESUME_MS);
  };

  CustomTrackCarousel.prototype.finishNativeScroll = function() {
    this.isUserScrolling = false;
    this.viewport.classList.remove('is-touch-scrolling');

    if (this.handleLoopBoundary()) {
      this.scheduleAutoplayResume();
      return;
    }

    var domIndex = this.getClosestDomIndex();
    this.currentIndex = this.domToLogicalIndex(domIndex);
    this.syncUi();
    this.scheduleAutoplayResume();
  };

  CustomTrackCarousel.prototype.logicalToDomIndex = function(logicalIndex) {
    if (this.loopOffset) {
      return logicalIndex + this.loopOffset;
    }
    return Math.max(0, Math.min(logicalIndex, this.realSlideCount - 1));
  };

  CustomTrackCarousel.prototype.getDomScrollLeft = function(domIndex) {
    var slide = this.slides[domIndex];
    if (!slide || !this.viewport) return 0;

    var vpWidth = this.viewport.clientWidth;
    var slideCenter = slide.offsetLeft + slide.offsetWidth / 2;
    var target = slideCenter - vpWidth / 2;
    var maxScroll = Math.max(0, this.viewport.scrollWidth - vpWidth);

    return Math.max(0, Math.min(target, maxScroll));
  };

  CustomTrackCarousel.prototype.getClosestDomIndex = function() {
    if (!this.viewport || this.slides.length === 0) return this.loopOffset;

    var scrollLeft = this.viewport.scrollLeft;
    var vpWidth = this.viewport.clientWidth;
    var viewportCenter = scrollLeft + vpWidth / 2;
    var closest = 0;
    var minDiff = Infinity;

    for (var i = 0; i < this.slides.length; i++) {
      var slide = this.slides[i];
      var slideCenter = slide.offsetLeft + slide.offsetWidth / 2;
      var diff = Math.abs(slideCenter - viewportCenter);
      if (diff < minDiff) {
        minDiff = diff;
        closest = i;
      }
    }

    return closest;
  };

  CustomTrackCarousel.prototype.domToLogicalIndex = function(domIndex) {
    if (!this.loopOffset) {
      return Math.max(0, Math.min(domIndex, this.realSlideCount - 1));
    }

    if (domIndex === 0) {
      return this.realSlideCount - 1;
    }

    if (domIndex === this.slides.length - 1) {
      return 0;
    }

    return domIndex - this.loopOffset;
  };

  CustomTrackCarousel.prototype.scrollToDomIndex = function(domIndex, instant) {
    if (!this.slides[domIndex]) return;

    var left = this.getDomScrollLeft(domIndex);

    if (instant) {
      this.isJumping = true;
      this.viewport.classList.add('is-dragging');
      this.viewport.scrollLeft = left;
      this.viewport.classList.remove('is-dragging');
      var self = this;
      requestAnimationFrame(function() {
        self.isJumping = false;
      });
    } else {
      this.viewport.scrollTo({ left: left, behavior: 'smooth' });
    }
  };

  CustomTrackCarousel.prototype.animateToCloneThenJump = function(cloneDom, realDom, logical, instant) {
    var self = this;

    if (instant || this.reducedMotion) {
      this.currentIndex = logical;
      this.scrollToDomIndex(realDom, true);
      this.syncUi();
      return;
    }

    this.scrollToDomIndex(cloneDom, false);
    clearTimeout(this.loopJumpTimer);
    this.loopJumpTimer = setTimeout(function() {
      self.currentIndex = logical;
      self.scrollToDomIndex(realDom, true);
      self.syncUi();
    }, 520);
  };

  CustomTrackCarousel.prototype.scrollToLogicalIndex = function(logicalIndex, instant) {
    if (this.realSlideCount === 0) return;

    var prev = this.currentIndex;
    var target = logicalIndex;

    if (this.loopEnabled && this.realSlideCount > 1) {
      var goingForward = logicalIndex > prev || (prev === this.realSlideCount - 1 && logicalIndex === 0);
      var goingBackward = logicalIndex < prev || (prev === 0 && logicalIndex === this.realSlideCount - 1);

      if (prev === this.realSlideCount - 1 && goingForward && logicalIndex !== prev) {
        this.animateToCloneThenJump(this.slides.length - 1, this.loopOffset, 0, instant);
        return;
      }

      if (prev === 0 && goingBackward && logicalIndex !== prev) {
        this.animateToCloneThenJump(0, this.realSlideCount, this.realSlideCount - 1, instant);
        return;
      }

      if (target < 0) target = this.realSlideCount - 1;
      if (target >= this.realSlideCount) target = 0;
    } else {
      target = Math.max(0, Math.min(target, this.realSlideCount - 1));
    }

    this.currentIndex = target;
    this.scrollToDomIndex(this.logicalToDomIndex(target), instant);
    this.syncUi();
  };

  CustomTrackCarousel.prototype.handleLoopBoundary = function() {
    if (!this.loopEnabled || this.realSlideCount <= 1 || this.isJumping) return false;

    var domIndex = this.getClosestDomIndex();

    if (domIndex === 0) {
      this.currentIndex = this.realSlideCount - 1;
      this.scrollToDomIndex(this.realSlideCount, true);
      this.syncUi();
      return true;
    }

    if (domIndex === this.slides.length - 1) {
      this.currentIndex = 0;
      this.scrollToDomIndex(this.loopOffset, true);
      this.syncUi();
      return true;
    }

    var logical = this.domToLogicalIndex(domIndex);
    if (logical !== this.currentIndex) {
      this.currentIndex = logical;
      this.syncUi();
    }

    return false;
  };

  CustomTrackCarousel.prototype.bindEvents = function() {
    var self = this;

    this.dots.forEach(function(dot) {
      dot.addEventListener('click', self.onDotClick);
    });

    if (this.prevBtn) this.prevBtn.addEventListener('click', this.onPrevClick);
    if (this.nextBtn) this.nextBtn.addEventListener('click', this.onNextClick);

    this.viewport.addEventListener('scroll', this.onScroll, { passive: true });

    if (this.realSlideCount > 1) {
      if (this.useNativeTouchScroll()) {
        this.viewport.addEventListener('touchstart', this.onTouchStart, { passive: true });
        this.viewport.addEventListener('touchend', this.onTouchEnd, { passive: true });
        this.viewport.addEventListener('touchcancel', this.onTouchEnd, { passive: true });
      } else {
        this.viewport.addEventListener('pointerdown', this.onPointerDown);
        this.viewport.addEventListener('pointermove', this.onPointerMove);
        this.viewport.addEventListener('pointerup', this.onPointerUp);
        this.viewport.addEventListener('pointercancel', this.onPointerCancel);
      }
    }

    if (this.pauseOnHover) {
      this.root.addEventListener('mouseenter', this.onMouseEnter);
      this.root.addEventListener('mouseleave', this.onMouseLeave);
      this.root.addEventListener('focusin', this.onFocusIn);
      this.root.addEventListener('focusout', this.onFocusOut);
    }

    document.addEventListener('visibilitychange', this.onVisibilityChange);
    window.addEventListener('resize', this.onResize);

    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(this.onResize);
      this.resizeObserver.observe(this.viewport);
      this.resizeObserver.observe(this.root);
    }

    this.mobileMq = window.matchMedia('(max-width: 749px)');
    if (this.mobileMq.addEventListener) {
      this.mobileMq.addEventListener('change', this.onBreakpointChange);
    } else if (this.mobileMq.addListener) {
      this.mobileMq.addListener(this.onBreakpointChange);
    }
  };

  CustomTrackCarousel.prototype.syncUi = function() {
    var self = this;

    this.dots.forEach(function(dot, i) {
      dot.classList.toggle('is-active', i === self.currentIndex);
      dot.setAttribute('aria-selected', i === self.currentIndex ? 'true' : 'false');
    });

    if (this.prevBtn) {
      if (this.loopEnabled || this.currentIndex > 0) {
        this.prevBtn.removeAttribute('disabled');
      } else {
        this.prevBtn.setAttribute('disabled', 'disabled');
      }
    }

    if (this.nextBtn) {
      if (this.loopEnabled || this.currentIndex < this.realSlideCount - 1) {
        this.nextBtn.removeAttribute('disabled');
      } else {
        this.nextBtn.setAttribute('disabled', 'disabled');
      }
    }

    this.slides.forEach(function(slide, domIndex) {
      var logical = self.domToLogicalIndex(domIndex);
      var isActive = logical === self.currentIndex && !slide.classList.contains('is-clone');
      if (slide.classList.contains('is-clone')) {
        slide.setAttribute('aria-hidden', 'true');
      } else {
        slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      }
    });
  };

  CustomTrackCarousel.prototype.onScroll = function() {
    if (this.isDragging || this.isJumping) return;

    if (this.isUserScrolling) {
      clearTimeout(this.scrollEndTimer);
      var selfTouch = this;
      this.scrollEndTimer = setTimeout(function() {
        selfTouch.finishNativeScroll();
      }, NATIVE_SCROLL_END_MS);
      return;
    }

    clearTimeout(this.scrollEndTimer);
    var self = this;
    this.scrollEndTimer = setTimeout(function() {
      if (self.isUserScrolling) return;
      if (self.handleLoopBoundary()) return;

      var domIndex = self.getClosestDomIndex();
      var targetScroll = self.getDomScrollLeft(domIndex);

      if (Math.abs(self.viewport.scrollLeft - targetScroll) > 3) {
        self.scrollToDomIndex(domIndex, false);
      } else {
        self.handleLoopBoundary();
      }
    }, 60);
  };

  CustomTrackCarousel.prototype.onTouchStart = function(event) {
    if (!this.useNativeTouchScroll()) return;
    if (event.target.closest('a, button')) return;

    this.isUserScrolling = true;
    this.userInteracting = true;
    this.viewport.classList.add('is-touch-scrolling');
    this.stopAutoplay();
    clearTimeout(this.autoplayResumeTimer);
  };

  CustomTrackCarousel.prototype.onTouchEnd = function() {
    if (!this.useNativeTouchScroll()) return;

    clearTimeout(this.scrollEndTimer);
    var self = this;
    this.scrollEndTimer = setTimeout(function() {
      self.finishNativeScroll();
    }, NATIVE_SCROLL_END_MS);
  };

  CustomTrackCarousel.prototype.onPrevClick = function() {
    this.scrollToLogicalIndex(this.currentIndex - 1, false);
    this.restartAutoplay();
  };

  CustomTrackCarousel.prototype.onNextClick = function() {
    this.scrollToLogicalIndex(this.currentIndex + 1, false);
    this.restartAutoplay();
  };

  CustomTrackCarousel.prototype.onDotClick = function(event) {
    var index = parseInt(event.currentTarget.dataset.index, 10);
    if (isNaN(index)) return;
    this.scrollToLogicalIndex(index, false);
    this.restartAutoplay();
  };

  CustomTrackCarousel.prototype.onPointerDown = function(event) {
    if (this.useNativeTouchScroll() || event.pointerType === 'touch') return;
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    if (event.target.closest('a, button')) return;

    this.isDragging = true;
    this.userInteracting = true;
    this.pointerId = event.pointerId;
    this.startX = event.clientX;
    this.startScrollLeft = this.viewport.scrollLeft;
    this.viewport.classList.add('is-dragging');
    this.root.classList.add('is-dragging');
    this.viewport.setPointerCapture(event.pointerId);
    this.stopAutoplay();
  };

  CustomTrackCarousel.prototype.onPointerMove = function(event) {
    if (!this.isDragging || event.pointerId !== this.pointerId) return;
    event.preventDefault();
    var delta = this.startX - event.clientX;
    this.viewport.scrollLeft = this.startScrollLeft + delta;
  };

  CustomTrackCarousel.prototype.onPointerUp = function(event) {
    if (!this.isDragging || event.pointerId !== this.pointerId) return;
    this.finishDrag(event.clientX);
  };

  CustomTrackCarousel.prototype.onPointerCancel = function(event) {
    if (!this.isDragging || event.pointerId !== this.pointerId) return;
    this.finishDrag(this.startX);
  };

  CustomTrackCarousel.prototype.finishDrag = function(endX) {
    this.isDragging = false;
    this.viewport.classList.remove('is-dragging');
    this.root.classList.remove('is-dragging');

    if (this.pointerId !== null) {
      try {
        this.viewport.releasePointerCapture(this.pointerId);
      } catch (e) {}
      this.pointerId = null;
    }

    var delta = this.startX - endX;
    if (Math.abs(delta) >= SWIPE_THRESHOLD) {
      if (delta > 0) this.scrollToLogicalIndex(this.currentIndex + 1, false);
      else this.scrollToLogicalIndex(this.currentIndex - 1, false);
    } else {
      var domIndex = this.getClosestDomIndex();
      this.scrollToDomIndex(domIndex, false);
    }

    var self = this;
    setTimeout(function() {
      self.handleLoopBoundary();
    }, 400);

    this.scheduleAutoplayResume();
  };

  CustomTrackCarousel.prototype.startAutoplay = function() {
    var self = this;
    this.stopAutoplay();
    if (!this.autoplayEnabled || this.realSlideCount <= 1 || this.isPaused || this.userInteracting) return;

    this.autoplayTimer = setInterval(function() {
      if (self.isPaused || self.userInteracting || self.isUserScrolling || document.hidden) return;
      self.scrollToLogicalIndex(self.currentIndex + 1, false);
    }, this.autoplaySpeed);
  };

  CustomTrackCarousel.prototype.stopAutoplay = function() {
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  };

  CustomTrackCarousel.prototype.restartAutoplay = function() {
    if (this.autoplayEnabled && !this.reducedMotion) this.startAutoplay();
  };

  CustomTrackCarousel.prototype.onMouseEnter = function() {
    this.isPaused = true;
    this.stopAutoplay();
  };

  CustomTrackCarousel.prototype.onMouseLeave = function() {
    this.isPaused = false;
    this.restartAutoplay();
  };

  CustomTrackCarousel.prototype.onFocusIn = function() {
    this.isPaused = true;
    this.stopAutoplay();
  };

  CustomTrackCarousel.prototype.onFocusOut = function(event) {
    if (this.root.contains(event.relatedTarget)) return;
    this.isPaused = false;
    this.restartAutoplay();
  };

  CustomTrackCarousel.prototype.onVisibilityChange = function() {
    if (document.hidden) this.stopAutoplay();
    else this.restartAutoplay();
  };

  CustomTrackCarousel.prototype.applyResize = function() {
    var savedIndex = this.currentIndex;
    this.updateDimensions();
    this.adjustCardContentSpacing();
    this.scrollToLogicalIndex(savedIndex, true);
  };

  CustomTrackCarousel.prototype.adjustCardContentSpacing = function() {
    var mobile = window.matchMedia('(max-width: 749px)').matches;
    var minGap = mobile ? 10 : 14;

    this.realSlides.forEach(function(slide) {
      var card = slide.querySelector('.custom-track-carousel__card');
      if (!card) return;

      var cardRect = card.getBoundingClientRect();
      if (cardRect.height <= 0) return;

      var heading = card.querySelector(
        mobile
          ? '.custom-track-carousel__card-heading--mobile'
          : '.custom-track-carousel__card-heading--desktop'
      );
      var text = card.querySelector(
        mobile ? '.custom-track-carousel__card-text--mobile' : '.custom-track-carousel__card-text--desktop'
      );
      var button = card.querySelector(
        mobile
          ? '.custom-track-carousel__card-button--mobile'
          : '.custom-track-carousel__card-button--desktop'
      );

      if (heading) {
        heading.style.top = '';
      }
      if (text) {
        text.style.top = '';
      }
      if (button) {
        button.style.top = '';
      }

      if (heading && text) {
        var headingRect = heading.getBoundingClientRect();
        var textRect = text.getBoundingClientRect();
        if (headingRect.bottom + minGap > textRect.top) {
          var nextTop = ((headingRect.bottom + minGap - cardRect.top) / cardRect.height) * 100;
          text.style.top = Math.min(nextTop, 72) + '%';
        }
      }

      if (text && button) {
        var textRect2 = text.getBoundingClientRect();
        var buttonRect = button.getBoundingClientRect();
        if (textRect2.bottom + minGap > buttonRect.top) {
          var buttonTop = ((textRect2.bottom + minGap - cardRect.top) / cardRect.height) * 100;
          button.style.top = Math.min(buttonTop, 82) + '%';
        }
      }
    });
  };

  CustomTrackCarousel.prototype.onResize = function() {
    var self = this;
    cancelAnimationFrame(this.resizeRaf);
    this.resizeRaf = requestAnimationFrame(function() {
      self.applyResize();
    });
  };

  CustomTrackCarousel.prototype.onBreakpointChange = function() {
    this.applyResize();
    this.rebindPointerAndTouch();
  };

  CustomTrackCarousel.prototype.rebindPointerAndTouch = function() {
    if (!this.viewport || this.realSlideCount <= 1) return;

    this.viewport.removeEventListener('pointerdown', this.onPointerDown);
    this.viewport.removeEventListener('pointermove', this.onPointerMove);
    this.viewport.removeEventListener('pointerup', this.onPointerUp);
    this.viewport.removeEventListener('pointercancel', this.onPointerCancel);
    this.viewport.removeEventListener('touchstart', this.onTouchStart);
    this.viewport.removeEventListener('touchend', this.onTouchEnd);
    this.viewport.removeEventListener('touchcancel', this.onTouchEnd);

    if (this.useNativeTouchScroll()) {
      this.viewport.addEventListener('touchstart', this.onTouchStart, { passive: true });
      this.viewport.addEventListener('touchend', this.onTouchEnd, { passive: true });
      this.viewport.addEventListener('touchcancel', this.onTouchEnd, { passive: true });
    } else {
      this.viewport.addEventListener('pointerdown', this.onPointerDown);
      this.viewport.addEventListener('pointermove', this.onPointerMove);
      this.viewport.addEventListener('pointerup', this.onPointerUp);
      this.viewport.addEventListener('pointercancel', this.onPointerCancel);
    }
  };

  CustomTrackCarousel.prototype.destroy = function() {
    this.stopAutoplay();
    clearTimeout(this.loopJumpTimer);
    clearTimeout(this.scrollEndTimer);
    clearTimeout(this.autoplayResumeTimer);
    this.removeClones();
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    window.removeEventListener('resize', this.onResize);

    if (this.mobileMq) {
      if (this.mobileMq.removeEventListener) {
        this.mobileMq.removeEventListener('change', this.onBreakpointChange);
      } else if (this.mobileMq.removeListener) {
        this.mobileMq.removeListener(this.onBreakpointChange);
      }
    }

    if (this.resizeObserver) this.resizeObserver.disconnect();
  };

  var instances = {};

  function initCustomTrackCarousel(id) {
    var root = document.getElementById(id);
    if (!root) return null;

    if (instances[id]) {
      instances[id].destroy();
      delete instances[id];
    }

    var instance = new CustomTrackCarousel(root);
    instances[id] = instance;
    return instance;
  }

  if (typeof window.initCustomTrackCarousel === 'undefined') {
    window.initCustomTrackCarousel = initCustomTrackCarousel;
  }

  document.addEventListener('shopify:section:load', function(event) {
    var root = event.target.querySelector('[id^="CustomTrackCarousel-"]');
    if (root && window.initCustomTrackCarousel) {
      window.initCustomTrackCarousel(root.id);
    }
  });

  document.addEventListener('shopify:section:unload', function(event) {
    var root = event.target.querySelector('[id^="CustomTrackCarousel-"]');
    if (root && instances[root.id]) {
      instances[root.id].destroy();
      delete instances[root.id];
    }
  });

  document.addEventListener('shopify:block:select', function(event) {
    var slide = event.target.closest('.custom-track-carousel__slide:not(.is-clone)');
    if (!slide) return;
    var root = slide.closest('[id^="CustomTrackCarousel-"]');
    if (!root || !instances[root.id]) return;
    var index = parseInt(slide.dataset.slideIndex, 10);
    if (!isNaN(index)) instances[root.id].scrollToLogicalIndex(index, false);
  });
})();
